import React, { useEffect, useState, useCallback } from 'react';
import { Search, TrendingDown } from 'lucide-react';
import { inventoryApi } from '../api/inventory';
import { productsApi } from '../api/products';
import { purchaseApi } from '../api/purchase';

interface StockItem {
  id:number; product_id:number; sku:string; product_name:string;
  category_name:string; unit_symbol:string; location_name:string; branch_name:string;
  qty_on_hand:number; qty_reserved:number; qty_available:number;
  cost_price:number; selling_price:number; min_reorder_level:number;
  inventory_value:number; potential_profit:number; below_reorder:boolean;
  costing_method:string;
}

const Inventory: React.FC = () => {
  const [items, setItems]           = useState<StockItem[]>([]);
  const [total, setTotal]           = useState(0);
  const [summary, setSummary]       = useState<any>(null);
  const [search, setSearch]         = useState('');
  const [categoryFilter, setCategory] = useState('');
  const [locationFilter, setLocation] = useState('');
  const [loading, setLoading]       = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [locations, setLocations]   = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<StockItem|null>(null);
  const [movements, setMovements]   = useState<any[]>([]);
  const [movLoading, setMovLoading] = useState(false);

  const loadMeta = () => {
    productsApi.categories().then(r => setCategories(r.data.data));
    purchaseApi.locations().then(r => setLocations(r.data.data));
  };

  const load = useCallback(() => {
    setLoading(true);
    inventoryApi.list({ search, category_id: categoryFilter, location_id: locationFilter }).then(res => {
      setItems(res.data.data);
      setTotal(res.data.total);
      setSummary(res.data.summary);
      setLoading(false);
    });
  }, [search, categoryFilter, locationFilter]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const showMovements = async (item: StockItem) => {
    setSelectedProduct(item);
    setMovLoading(true);
    const res = await inventoryApi.movements(item.product_id);
    setMovements(res.data.data);
    setMovLoading(false);
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Inventory</h1>
        {summary && (
          <div style={{ fontSize:11, color:'var(--tx3)' }}>
            {summary.below_reorder > 0 && (
              <span style={{ color:'var(--red)', fontWeight:600 }}>
                <TrendingDown size={12} style={{marginRight:4}} />
                {summary.below_reorder} items below reorder level
              </span>
            )}
          </div>
        )}
      </div>

      {summary && (
        <div className="kpi-grid" style={{ marginBottom:16 }}>
          <div className="kpi-card">
            <div className="kpi-label">Total Products</div>
            <div className="kpi-value" style={{ color:'var(--blue)' }}>{summary.total_products}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Inventory Value</div>
            <div className="kpi-value" style={{ fontSize:18, color:'var(--tx)' }}>{Number(summary.total_value).toFixed(2)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Potential Revenue</div>
            <div className="kpi-value" style={{ fontSize:18, color:'var(--green)' }}>{Number(summary.potential_revenue).toFixed(2)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Potential Profit</div>
            <div className="kpi-value" style={{ fontSize:18, color:'var(--purple)' }}>{Number(summary.potential_profit).toFixed(2)}</div>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <select className="filter-pill" value={categoryFilter} onChange={e => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="filter-pill" value={locationFilter} onChange={e => setLocation(e.target.value)}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <span className="record-count">{total} products</span>
      </div>

      {selectedProduct && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <span>Stock Movements - {selectedProduct.product_name}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedProduct(null)}>Close</button>
          </div>
          <table>
            <thead><tr>
              <th>Type</th><th>From</th><th>To</th>
              <th style={{textAlign:'right'}}>Qty</th>
              <th style={{textAlign:'right'}}>Unit Cost</th>
              <th style={{textAlign:'right'}}>Cost Impact</th>
              <th>Date</th>
            </tr></thead>
            <tbody>
              {movLoading ? (
                <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--tx3)'}}>Loading...</td></tr>
              ) : movements.length === 0 ? (
                <tr><td colSpan={7}><div className="empty-state"><p>No movements.</p></div></td></tr>
              ) : movements.map(m => (
                <tr key={m.id}>
                  <td><span className={`badge ${m.qty > 0 ? 'badge-green' : 'badge-red'}`}>{m.move_type}</span></td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{m.from_location || '-'}</td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{m.to_location || '-'}</td>
                  <td className="td-num" style={{color: m.qty > 0 ? 'var(--green)' : 'var(--red)'}}>{m.qty > 0 ? '+' : ''}{m.qty}</td>
                  <td className="td-num">{Number(m.unit_cost).toFixed(2)}</td>
                  <td className="td-num" style={{color: m.total_cost_impact > 0 ? 'var(--green)' : 'var(--red)'}}>{Number(m.total_cost_impact).toFixed(2)}</td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(m.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>SKU</th><th>Product</th><th>Category</th><th>Location</th>
            <th style={{textAlign:'right'}}>On Hand</th>
            <th style={{textAlign:'right'}}>Reserved</th>
            <th style={{textAlign:'right'}}>Available</th>
            <th style={{textAlign:'right'}}>Cost Price</th>
            <th style={{textAlign:'right'}}>Value</th>
            <th>Status</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><p>No inventory yet.</p></div></td></tr>
            ) : items.map(item => (
              <tr key={item.id} onClick={() => showMovements(item)} style={{cursor:'pointer'}} className={item.below_reorder ? 'tr-warning' : ''}>
                <td className="td-code">{item.sku}</td>
                <td style={{fontWeight:600}}>{item.product_name}</td>
                <td style={{color:'var(--tx3)'}}>{item.category_name}</td>
                <td style={{color:'var(--tx3)'}}>{item.location_name}</td>
                <td className="td-num">{Number(item.qty_on_hand).toFixed(2)}</td>
                <td className="td-num" style={{color:'var(--amber)'}}>{Number(item.qty_reserved).toFixed(2)}</td>
                <td className="td-num td-green">{Number(item.qty_available).toFixed(2)}</td>
                <td className="td-num">{Number(item.cost_price).toFixed(2)}</td>
                <td className="td-num td-green">{Number(item.inventory_value).toFixed(2)}</td>
                <td>{item.below_reorder ? <span className="badge badge-red">Low Stock</span> : <span className="badge badge-green">OK</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Inventory;
